import React, { useRef, useState } from "react";
import { FiArrowRight, FiRefreshCw } from "react-icons/fi";
import { courseModules, lessons } from "./lessons";
import { moduleReviews } from "./courseReviews";
import "./courseReview.css";

const storageKey = "finance-learning-reviews-v1";
const reviews = courseModules.map(module => ({ ...moduleReviews.find(review => review.moduleId === module.id), module }))
  .filter(review => Array.isArray(review.questions) && review.questions.length > 0);

function readScores() {
  try {
    const raw = JSON.parse(localStorage.getItem(storageKey) || "{}");
    const scores = {};
    if (raw && typeof raw === "object" && !Array.isArray(raw)) {
      reviews.forEach(review => {
        const value = raw[review.moduleId];
        const count = review.questions.length;
        if (value && value.total === count && Number.isInteger(value.latest) && Number.isInteger(value.best)
          && value.latest >= 0 && value.latest <= value.best && value.best <= count
          && Number.isSafeInteger(value.attempts) && value.attempts > 0) {
          scores[review.moduleId] = { latest: value.latest, best: value.best, total: count, attempts: value.attempts };
        }
      });
    }
    return { scores, storageError: false };
  } catch {
    return { scores: {}, storageError: true };
  }
}

export default function CourseReview({ onOpenLesson }) {
  const [activeId, setActiveId] = useState(reviews[0]?.moduleId);
  const [attempts, setAttempts] = useState({});
  const [saved, setSaved] = useState(readScores);
  const resultRef = useRef(null);
  const headingRef = useRef(null);
  const review = reviews.find(item => item.moduleId === activeId);
  if (!review) return null;
  const attempt = attempts[activeId] || { answers: {}, submitted: false };
  const count = review.questions.length;
  const answered = review.questions.filter(question => Number.isInteger(attempt.answers[question.id])).length;
  const score = review.questions.filter(question => attempt.answers[question.id] === question.answer).length;
  const previous = saved.scores[activeId];

  const chooseAnswer = (questionId, value) => {
    if (attempt.submitted) return;
    setAttempts(current => ({ ...current, [activeId]: { ...attempt, answers: { ...attempt.answers, [questionId]: value } } }));
  };
  const submit = () => {
    if (attempt.submitted || answered !== count) return;
    setAttempts(current => ({ ...current, [activeId]: { ...attempt, submitted: true } }));
    const nextScores = { ...saved.scores, [activeId]: { latest: score, best: Math.max(previous?.best || 0, score), total: count, attempts: (previous?.attempts || 0) + 1 } };
    let storageError = false;
    try { localStorage.setItem(storageKey, JSON.stringify(nextScores)); } catch { storageError = true; }
    setSaved({ scores: nextScores, storageError });
    requestAnimationFrame(() => resultRef.current?.focus());
  };
  const retry = () => {
    setAttempts(current => ({ ...current, [activeId]: { answers: {}, submitted: false } }));
    requestAnimationFrame(() => headingRef.current?.focus());
  };

  return <section className="finance-review" aria-labelledby="finance-review-title">
    <div className="finance-section-heading"><div><span className="finance-eyebrow">PUT IT INTO PRACTICE</span><h2 id="finance-review-title">用生活情境，檢查自己懂了多少</h2><p>每個單元四題。全部作答後再看解析，答錯的地方可以回課文複習。</p></div></div>
    <nav className="finance-review-modules" aria-label="Review modules">{reviews.map(item => <button type="button" key={item.moduleId} aria-pressed={activeId === item.moduleId} onClick={() => setActiveId(item.moduleId)}><strong>{item.module.label}</strong><span>{saved.scores[item.moduleId] ? `最佳 ${saved.scores[item.moduleId].best}/${item.questions.length}` : "尚未完成測驗"}</span></button>)}</nav>
    <div className="finance-panel finance-review-paper">
      <header className="finance-review-heading"><span className="finance-tag">{review.module.label} · REVIEW</span><h3 tabIndex={-1} ref={headingRef}>{review.title}</h3><p>{previous ? `上次 ${previous.latest} / ${previous.total} 題 · 最佳 ${previous.best} / ${previous.total} 題 · 已練習 ${previous.attempts} 次` : "還沒有成績紀錄，先試試看。"}</p></header>
      <p className="finance-local-note finance-review-storage">提交後只保存各單元的上次與最佳成績，紀錄僅存在這個瀏覽器。本頁內切換課程可保留作答；關閉或重新整理頁面後，作答內容會清除；清除網站資料也會刪除成績。測驗不會代替課程完成紀錄。</p>
      {saved.storageError && <p className="finance-review-storage-error" role="alert">瀏覽器無法讀取或保存測驗紀錄。你仍可作答，但請先自行記下成績，重新整理後可能無法保留。</p>}
      {attempt.submitted && <section className="finance-review-result" aria-label="測驗結果" role="status" tabIndex={-1} ref={resultRef}><span>這次答對</span><strong>{score} / {count} 題</strong><p>{score === count ? "全部答對！可以換下一個單元，或重新練習一次。" : "看看下方解析，再回到相關課文把觀念補起來。"}</p></section>}
      <div className="finance-review-questions">{review.questions.map((question, index) => {
        const answer = attempt.answers[question.id];
        const correct = answer === question.answer;
        const relatedLesson = lessons.find(lesson => lesson.id === question.lessonId);
        return <fieldset key={question.id} className="finance-review-question"><legend><span>第 {index + 1} 題</span>{question.question}</legend><div className="finance-quiz-options">{question.options.map((option, optionIndex) => <button type="button" key={option} aria-pressed={answer === optionIndex} disabled={attempt.submitted} className={attempt.submitted && answer === optionIndex ? (correct ? "is-correct" : "is-incorrect") : ""} onClick={() => chooseAnswer(question.id, optionIndex)}><span aria-hidden="true">{String.fromCharCode(65 + optionIndex)}</span>{option}</button>)}</div>{attempt.submitted && <div className={`finance-review-explanation ${correct ? "is-correct" : "is-incorrect"}`}><strong>{correct ? "答對了" : "這題再複習"}</strong><p>正確答案：{question.options[question.answer]}</p><p>{question.explanation}</p>{!correct && relatedLesson && onOpenLesson && <button type="button" className="finance-text-button" onClick={() => onOpenLesson(question.lessonId)}>複習：{relatedLesson.title}<FiArrowRight aria-hidden="true" /></button>}</div>}</fieldset>;
      })}</div>
      <div className="finance-review-actions">{attempt.submitted ? <button type="button" className="finance-primary" onClick={retry}><FiRefreshCw aria-hidden="true" />重新練習這個單元</button> : <><span aria-live="polite">已回答 {answered} / {count} 題{answered < count ? `，還差 ${count - answered} 題。` : "，可以查看結果了。"}</span><button type="button" className="finance-primary" disabled={answered !== count} onClick={submit}>提交答案，查看解析<FiArrowRight aria-hidden="true" /></button></>}</div>
    </div>
  </section>;
}
